import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calculator, Receipt, TrendingUp } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDirection } from '@/contexts/DirectionContext';
import { useCostEstimate, useCostTariffs } from '../hooks/useCosts';
import EstimatePanel from '../components/EstimatePanel';
import TariffsPanel from '../components/TariffsPanel';
import CostCalculator from '../components/CostCalculator';

type Tab = 'estimate' | 'tariffs' | 'calculator';
const TABS: Tab[] = ['estimate', 'tariffs', 'calculator'];

/**
 * „Costuri": estimarea costurilor pe transporturile clientului (navlu ofertat +
 * cheltuieli locale + contrastalii/depozitare în curs), tarifele standard TLS și
 * un calculator. Doar import — pe export pagina nu apare în meniu.
 */
const CostsIndex: React.FC = () => {
  const { currentLanguage: lang } = useLanguage();
  const isRo = lang === 'ro';
  const { direction } = useDirection();
  const isExport = direction === 'export';
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab') as Tab | null;
  const tab: Tab = requested && TABS.includes(requested) ? requested : 'estimate';

  const tariffs = useCostTariffs();
  const estimate = useCostEstimate();

  useEffect(() => {
    document.body.classList.add('dashboard-claude-bg');
    return () => document.body.classList.remove('dashboard-claude-bg');
  }, []);

  const setTab = (next: Tab) => {
    const p = new URLSearchParams(params);
    if (next === 'estimate') p.delete('tab');
    else p.set('tab', next);
    setParams(p, { replace: true });
  };

  const tabs: { id: Tab; icon: typeof Receipt; ro: string; en: string; shortRo: string; shortEn: string }[] = [
    { id: 'estimate', icon: TrendingUp, ro: 'Estimare costuri', en: 'Cost estimate', shortRo: 'Estimare', shortEn: 'Estimate' },
    { id: 'tariffs', icon: Receipt, ro: 'Tarifele noastre', en: 'Our tariffs', shortRo: 'Tarife', shortEn: 'Tariffs' },
    { id: 'calculator', icon: Calculator, ro: 'Calculator', en: 'Calculator', shortRo: 'Calculator', shortEn: 'Calculator' },
  ];

  return (
    <div style={{ color: 'var(--text)' }}>
      <div className="ctn-page-head">
        <h1>{isRo ? 'Costuri' : 'Costs'}</h1>
        <div className="muted fs-13 mt-1 hidden-mobile">
          {isRo
            ? 'Costurile estimate pentru transporturile dumneavoastră și tarifele standard aplicate în portul Constanța.'
            : 'Estimated costs for your shipments and the standard tariffs applied in the port of Constanța.'}
        </div>
      </div>

      {isExport ? (
        <div className="card" style={{ padding: 0 }}>
          <div className="empty">
            <div className="icon">
              <Receipt size={22} />
            </div>
            <div className="fw-600">
              {isRo ? 'Disponibil pentru importuri' : 'Available for imports'}
            </div>
            <div className="muted fs-13 mt-1">
              {isRo
                ? 'Estimarea costurilor și tarifele de terminal se aplică transporturilor descărcate în Constanța. Comutați pe Import din antet pentru a le vedea.'
                : 'Cost estimates and terminal tariffs apply to shipments discharged in Constanța. Switch to Import in the header to see them.'}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="ctn-chips cst-tabs" role="tablist" aria-label={isRo ? 'Secțiuni' : 'Sections'}>
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={'ctn-chip cst-tab' + (active ? ' active' : '')}
                  onClick={() => setTab(t.id)}
                >
                  <Icon size={14} strokeWidth={2} />
                  <span className="cst-tab-long">{isRo ? t.ro : t.en}</span>
                  <span className="cst-tab-short">{isRo ? t.shortRo : t.shortEn}</span>
                </button>
              );
            })}
          </div>

          {tab === 'estimate' && (
            <EstimatePanel
              estimate={estimate.data}
              isLoading={estimate.isLoading}
              error={estimate.error as Error | null}
              onRetry={() => void estimate.refetch()}
              onShowTariffs={() => setTab('tariffs')}
            />
          )}
          {tab === 'tariffs' && (
            <TariffsPanel tariffs={tariffs.data} isLoading={tariffs.isLoading} error={tariffs.error as Error | null} />
          )}
          {tab === 'calculator' && <CostCalculator tariffs={tariffs.data} isLoading={tariffs.isLoading} />}
        </>
      )}
    </div>
  );
};

export default CostsIndex;
